import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Column, DataRow, Typography } from '@tempmee/react';
import { Spinner } from '../../../commonComponents/Spinner';
import { ReviewCard } from './ReviewCard';
import { orNotAvailable } from './utils';

/**
 * The professional's review of the office, for one shift.
 *
 * Reads organization-service via `useGetOfficeReviewByShift` — the only
 * endpoint carrying this direction. Its three rows are the fields GOTU-6521
 * started capturing and that had nowhere to appear until now; the card it
 * replaces showed a `Rating` label with no value and a
 * `Confidential Feedback for GoTu` that was always `N/A`, because the monolith
 * never populated that field for a professional→office review.
 *
 * **Three states, and the order of the branches matters.** A failure has to be
 * read before the rows, because a failed request and a shift nobody reviewed
 * both arrive as a null `review` — rendering the rows in the failure case would
 * show `N/A` throughout, which reads as "nobody reviewed this shift" and is a
 * claim about the data rather than about the request.
 *
 * Absence is not a state of its own: the rows render either way, filled with
 * `N/A`. That is a deliberate call on GOTU-7339 — it keeps both cards the same
 * shape whether or not a review exists, at the cost of not distinguishing "no
 * review" from "a review with empty fields". The star is the only remaining
 * signal, since a card with no review has no rating to draw.
 */
export const OfficeReviewCard = ({ officeName, review, isPending, isError, onRetry }) => {
  const body = () => {
    if (isPending) {
      return (
        <Box py={4}>
          <Column align="center">
            <Spinner />
          </Column>
        </Box>
      );
    }

    // Inline and one line, not an `Alert`: this is a 464px card sitting beside
    // another one, and a full-width Alert sized for a screen surface would
    // displace its sibling.
    if (isError) {
      return (
        <Column gap={2} align="start" py={2}>
          <Typography variant="body2" className="text-error">
            Couldn&apos;t load this review.
          </Typography>
          <Button variant="outlined" color="secondary" size="small" onClick={onRetry}>
            Retry
          </Button>
        </Column>
      );
    }

    // No `!review` branch: the rows render either way, and `orNotAvailable`
    // turns every absent value into `N/A` — including the whole review being
    // absent, where each field reads from `undefined`.
    return (
      <>
        <DataRow
          label="Would you work here again?"
          value={orNotAvailable(review?.wouldWorkHereAgainLabel)}
        />
        <DataRow
          orientation="stacked"
          label="Office Feedback"
          value={orNotAvailable(review?.publicFeedback)}
        />
        <DataRow
          orientation="stacked"
          label="Private Feedback"
          value={orNotAvailable(review?.confidentialFeedback)}
        />
      </>
    );
  };

  return (
    <ReviewCard
      label="Office review"
      // The entity's own name once it has loaded, falling back to whatever the
      // page already knows — so an empty or failed card still says which office
      // the missing review is about.
      name={review?.officeName || officeName}
      rating={review?.rating}
    >
      {body()}
    </ReviewCard>
  );
};

OfficeReviewCard.propTypes = {
  /** Fallback heading, from the job, for when there is no review to name it. */
  officeName: PropTypes.string,
  /** The mapped review, or null for "no review" *and* for a failure. */
  review: PropTypes.shape({
    officeName: PropTypes.string,
    rating: PropTypes.number,
    wouldWorkHereAgainLabel: PropTypes.oneOf(['Yes', 'No', '']),
    publicFeedback: PropTypes.string,
    confidentialFeedback: PropTypes.string,
  }),
  isPending: PropTypes.bool,
  isError: PropTypes.bool,
  onRetry: PropTypes.func.isRequired,
};

OfficeReviewCard.defaultProps = {
  officeName: '',
  review: null,
  isPending: false,
  isError: false,
};

export default OfficeReviewCard;
